import { Box3, MathUtils, Vector3 } from 'three';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer';
import { Line2 } from 'three/examples/jsm/lines/Line2';
import { getUserData } from './types';
import { getMeasurableObjects } from './scene-utils';
const TEXT_SIZE = '14px';
const TEXT_COLOR = 'rgb(255,255,255)';
const BACKGROUND_COLOR = 'rgb(69,69,69)';
const BORDER_RADIUS = '15px';
const PADDING = '9px 13px 7px 13px';
const LABEL_OFFSET = 0.07;
const MAIN_LINE_COLOR = 0x454545;
const LINE_COLOR = 0x7b7c7c;
const LINE_OFFSET = 0.1;
const ARROW_OFFSET_1 = 0.02;
const ARROW_OFFSET_2 = 0.09;
const ARROW_OFFSET_3 = 0.11;
const WIDTH_ANGLE_RANGE = { start: 95, end: 265 };
const DEPTH_ANGLE_RANGE = { start: 10, end: 190 };
const HEIGHT_ANGLE_RANGE = { start: 175, end: 285 };
const UNITS = ' mm';
export class DimensionsRenderer {
    constructor(width, height) {
        this.width = true;
        this.depth = true;
        this.height = true;
        this.dimensionsRenderer2D = new CSS2DRenderer();
        this.dimensionsRenderer2D.setSize(width, height);
        this.dimensionsRenderer2D.domElement.style.position = 'absolute';
        this.dimensionsRenderer2D.domElement.style.top = '0px';
        this.dimensionsRenderer2D.domElement.style.transition = 'all .15s';
        this.dimensionsRenderer2D.domElement.style.pointerEvents = 'none';
        if (document.getElementById('viewer')) {
            document.getElementById('viewer').appendChild(this.dimensionsRenderer2D.domElement);
        }
    }
    render(dimensionScene2D, dimensionLinesNode, measurableObject, camera, angle, showRuler) {
        if (showRuler) {
            const angleInDegrees = MathUtils.radToDeg(angle) < 0 ? 180 + (180 + MathUtils.radToDeg(angle)) : MathUtils.radToDeg(angle);
            this.width = this.rebuildRuler(angleInDegrees, WIDTH_ANGLE_RANGE.start, WIDTH_ANGLE_RANGE.end, dimensionScene2D, dimensionLinesNode, measurableObject, this.width);
            this.depth = this.rebuildRuler(angleInDegrees, DEPTH_ANGLE_RANGE.start, DEPTH_ANGLE_RANGE.end, dimensionScene2D, dimensionLinesNode, measurableObject, this.depth);
            this.height = this.rebuildRuler(angleInDegrees, HEIGHT_ANGLE_RANGE.start, HEIGHT_ANGLE_RANGE.end, dimensionScene2D, dimensionLinesNode, measurableObject, this.height);
            this.dimensionsRenderer2D.render(dimensionScene2D, camera);
        }
    }
    rebuildRuler(angle, startAngle, endAngle, dimensionScene2D, dimensionLinesNode, measurableObject, rebuildFlag) {
        if (rebuildFlag && angle > startAngle && angle < endAngle) {
            rebuildFlag = false;
            this.clearScene(dimensionScene2D, dimensionLinesNode);
            this.buildRuler(dimensionScene2D, dimensionLinesNode, measurableObject, angle);
        }
        else {
            if (!rebuildFlag && !(angle > startAngle && angle < endAngle)) {
                rebuildFlag = true;
                this.clearScene(dimensionScene2D, dimensionLinesNode);
                this.buildRuler(dimensionScene2D, dimensionLinesNode, measurableObject, angle);
            }
        }
        return rebuildFlag;
    }
    clearScene(dimensionScene2D, dimensionLinesNode) {
        dimensionScene2D.remove(...dimensionScene2D.children);
        dimensionLinesNode.remove(...dimensionLinesNode.children);
    }
    buildRuler(dimensionScene2D, dimensionLinesNode, root, angle = -1) {
        this.clearScene(dimensionScene2D, dimensionLinesNode);
        const sceneBox = new Box3();
        const sceneBoxSize = new Vector3();
        const sceneBoxCenter = new Vector3();
        const measurableObject = getMeasurableObjects(root);
        if (measurableObject.children.length > 0) {
            sceneBox.setFromObject(measurableObject);
            sceneBox.getSize(sceneBoxSize);
            sceneBox.getCenter(sceneBoxCenter);
            const widthFlip = angle > WIDTH_ANGLE_RANGE.start && angle < WIDTH_ANGLE_RANGE.end;
            const widthLabelPosition = new Vector3(sceneBoxCenter.x, sceneBox.min.y + LABEL_OFFSET, widthFlip ? sceneBox.min.z - LINE_OFFSET : sceneBox.max.z + LINE_OFFSET);
            const widthLineStart = [
                sceneBox.min.x,
                sceneBox.min.y,
                widthFlip ? sceneBox.min.z - LINE_OFFSET : sceneBox.max.z + LINE_OFFSET,
            ];
            const widthLineEnd = [
                sceneBox.max.x,
                sceneBox.min.y,
                widthFlip ? sceneBox.min.z - LINE_OFFSET : sceneBox.max.z + LINE_OFFSET,
            ];
            const widthMinX = sceneBox.min.x + ARROW_OFFSET_1;
            const widthMaxX = sceneBox.max.x - ARROW_OFFSET_1;
            this.add2DLabel(widthLabelPosition, this.formatValue(sceneBoxSize.x), dimensionScene2D);
            this.add3DLine(widthLineStart, widthLineEnd, 1, dimensionLinesNode, MAIN_LINE_COLOR);
            this.add3DLine(widthLineStart, [widthMinX, sceneBox.min.y, widthFlip ? sceneBox.min.z - ARROW_OFFSET_2 : sceneBox.max.z + ARROW_OFFSET_2], 1.2, dimensionLinesNode, LINE_COLOR);
            this.add3DLine(widthLineStart, [widthMinX, sceneBox.min.y, widthFlip ? sceneBox.min.z - ARROW_OFFSET_3 : sceneBox.max.z + ARROW_OFFSET_3], 1.2, dimensionLinesNode, LINE_COLOR);
            this.add3DLine(widthLineEnd, [widthMaxX, sceneBox.min.y, widthFlip ? sceneBox.min.z - ARROW_OFFSET_2 : sceneBox.max.z + ARROW_OFFSET_2], 1.2, dimensionLinesNode, LINE_COLOR);
            this.add3DLine(widthLineEnd, [widthMaxX, sceneBox.min.y, widthFlip ? sceneBox.min.z - ARROW_OFFSET_3 : sceneBox.max.z + ARROW_OFFSET_3], 1.2, dimensionLinesNode, LINE_COLOR);
            const depthFlip = angle > DEPTH_ANGLE_RANGE.start && angle < DEPTH_ANGLE_RANGE.end;
            const depthLabelPosition = new Vector3(depthFlip ? sceneBox.max.x + LINE_OFFSET : sceneBox.min.x - LINE_OFFSET, sceneBox.min.y + LABEL_OFFSET, sceneBoxCenter.z);
            const depthLineStart = [
                depthFlip ? sceneBox.max.x + LINE_OFFSET : sceneBox.min.x - LINE_OFFSET,
                sceneBox.min.y,
                sceneBox.max.z,
            ];
            const depthLineEnd = [
                depthFlip ? sceneBox.max.x + LINE_OFFSET : sceneBox.min.x - LINE_OFFSET,
                sceneBox.min.y,
                sceneBox.min.z,
            ];
            const depthMinZ = sceneBox.min.z + ARROW_OFFSET_1;
            const depthMaxZ = sceneBox.max.z - ARROW_OFFSET_1;
            this.add2DLabel(depthLabelPosition, this.formatValue(sceneBoxSize.z), dimensionScene2D);
            this.add3DLine(depthLineStart, depthLineEnd, 1, dimensionLinesNode, MAIN_LINE_COLOR);
            this.add3DLine(depthLineEnd, [depthFlip ? sceneBox.max.x + ARROW_OFFSET_2 : sceneBox.min.x - ARROW_OFFSET_2, sceneBox.min.y, depthMinZ], 1.2, dimensionLinesNode, LINE_COLOR);
            this.add3DLine(depthLineEnd, [depthFlip ? sceneBox.max.x + ARROW_OFFSET_3 : sceneBox.min.x - ARROW_OFFSET_3, sceneBox.min.y, depthMinZ], 1.2, dimensionLinesNode, LINE_COLOR);
            this.add3DLine(depthLineStart, [depthFlip ? sceneBox.max.x + ARROW_OFFSET_2 : sceneBox.min.x - ARROW_OFFSET_2, sceneBox.min.y, depthMaxZ], 1.2, dimensionLinesNode, LINE_COLOR);
            this.add3DLine(depthLineStart, [depthFlip ? sceneBox.max.x + ARROW_OFFSET_3 : sceneBox.min.x - ARROW_OFFSET_3, sceneBox.min.y, depthMaxZ], 1.2, dimensionLinesNode, LINE_COLOR);
            const heightFlip = angle > HEIGHT_ANGLE_RANGE.start && angle < HEIGHT_ANGLE_RANGE.end;
            const heightLabelPosition = new Vector3(heightFlip ? sceneBox.min.x : sceneBox.max.x, sceneBoxCenter.y, heightFlip ? sceneBox.min.z - LINE_OFFSET : sceneBox.max.z + LINE_OFFSET);
            const heightLineStart = [
                heightFlip ? sceneBox.min.x - LINE_OFFSET : sceneBox.max.x + LINE_OFFSET,
                sceneBox.min.y,
                heightFlip ? sceneBox.min.z - LINE_OFFSET : sceneBox.max.z + LINE_OFFSET,
            ];
            const heightLineEnd = [
                heightFlip ? sceneBox.min.x - LINE_OFFSET : sceneBox.max.x + LINE_OFFSET,
                sceneBox.max.y,
                heightFlip ? sceneBox.min.z - LINE_OFFSET : sceneBox.max.z + LINE_OFFSET,
            ];
            const heightX = heightFlip ? sceneBox.min.x - LINE_OFFSET : sceneBox.max.x + LINE_OFFSET;
            const heightMinY = sceneBox.min.y + ARROW_OFFSET_1;
            const heightMaxY = sceneBox.max.y - ARROW_OFFSET_1;
            this.add2DLabel(heightLabelPosition, this.formatValue(sceneBoxSize.y), dimensionScene2D, -90);
            this.add3DLine(heightLineStart, heightLineEnd, 1, dimensionLinesNode, MAIN_LINE_COLOR);
            this.add3DLine(heightLineStart, [heightX, heightMinY, heightFlip ? sceneBox.min.z - ARROW_OFFSET_2 : sceneBox.max.z + ARROW_OFFSET_2], 1, dimensionLinesNode, LINE_COLOR);
            this.add3DLine(heightLineStart, [heightX, heightMinY, heightFlip ? sceneBox.min.z - ARROW_OFFSET_3 : sceneBox.max.z + ARROW_OFFSET_3], 1.2, dimensionLinesNode, LINE_COLOR);
            this.add3DLine(heightLineEnd, [heightX, heightMaxY, heightFlip ? sceneBox.min.z - ARROW_OFFSET_2 : sceneBox.max.z + ARROW_OFFSET_2], 1.2, dimensionLinesNode, LINE_COLOR);
            this.add3DLine(heightLineEnd, [heightX, heightMaxY, heightFlip ? sceneBox.min.z - ARROW_OFFSET_3 : sceneBox.max.z + ARROW_OFFSET_3], 1.2, dimensionLinesNode, LINE_COLOR);
        }
    }
    getMeasurableObject(root) {
        const measurableObject = root.clone(true);
        const toDelete = [];
        measurableObject.traverse((node) => {
            const takePlace = getUserData(node).takePlace;
            if (!takePlace) {
                toDelete.push(node);
            }
        });
        for (const node of toDelete) {
            node.removeFromParent();
        }
        return measurableObject;
    }
    add2DLabel(position, content, dimensionScene2D, rotation = 0) {
        const text = document.createElement('div');
        text.textContent = content;
        text.style.fontSize = TEXT_SIZE;
        text.style.color = TEXT_COLOR;
        text.style.transform = `rotate(${rotation}deg)`;
        text.style.backgroundColor = BACKGROUND_COLOR;
        text.style.borderRadius = BORDER_RADIUS;
        text.style.padding = PADDING;
        const wrap = document.createElement('div');
        wrap.appendChild(text);
        const label = new CSS2DObject(wrap);
        label.position.copy(position);
        dimensionScene2D.add(label);
    }
    add3DLine(from, to, width, dimensionLinesNode, color, opacity = 0.5) {
        const geometry = new LineGeometry().setPositions([...from, ...to]);
        const material = new LineMaterial({ color: color, linewidth: width, transparent: true });
        material.opacity = opacity;
        const line = new Line2(geometry, material);
        dimensionLinesNode.add(line);
    }
    formatValue(size) {
        const mm = size * 1000;
        const roundedToCm = Math.round(mm / 10) * 10;
        return roundedToCm + UNITS;
    }
}
//# sourceMappingURL=DimensionsRenderer.js.map